import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CrownShopModal.css';
import TooltipBadge from './shared/TooltipBadge';
import { formatCurrency } from './shared/formatCompact';

const API = process.env.REACT_APP_SERVER_URL || '';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TABS = [
  { key: 'items',     label: 'Items',     icon: '🛒' },
  { key: 'cosmetics', label: 'Cosmetics', icon: '✨' },
  { key: 'crowns',    label: 'Buy Crowns', icon: '💎' },
  { key: 'patron',    label: 'Patron',     icon: '⭐' },
  { key: 'chronicler', label: 'Chronicler', icon: '📜' },
];

const CrownShopModal = ({ character, onClose, onCharacterUpdate }) => {
  const [tab, setTab] = useState('items');
  const [shopData, setShopData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const crowns = character?.crowns || 0;

  const fetchData = useCallback(async () => {
    try {
      const [shopRes, pkgRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/billing/shop`, authHeaders()),
        axios.get(`${API}/api/billing/packages`, authHeaders()),
        axios.get(`${API}/api/billing/status`, authHeaders()),
      ]);
      setShopData(shopRes.data);
      // v637: server returns `crownPackages`, not `packages`
      setPackages(pkgRes.data.crownPackages || pkgRes.data.packages || []);
      setStatus(statusRes.data);
    } catch (err) {
      console.error('Crown shop load error:', err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const flash = (msg, isError) => {
    setMessage({ text: msg, isError });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleBuyItem = async (itemId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/billing/buy-item`, { itemId }, authHeaders());
      flash(res.data.message || 'Purchased!', false);
      if (onCharacterUpdate) onCharacterUpdate();
      fetchData();
    } catch (err) {
      flash(err.response?.data?.error || 'Purchase failed', true);
    }
    setBusy(false);
  };

  const handleBuyCosmetic = async (cosmeticId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/billing/buy-cosmetic`, { cosmeticId }, authHeaders());
      flash(res.data.message || 'Unlocked!', false);
      if (onCharacterUpdate) onCharacterUpdate();
      fetchData();
    } catch (err) {
      flash(err.response?.data?.error || 'Purchase failed', true);
    }
    setBusy(false);
  };

  const handleEquipCosmetic = async (cosmeticId, category) => {
    if (busy) return;
    setBusy(true);
    try {
      // v637: server requires `slot` (camelCase) derived from cosmetic category
      const slotMap = { chat_color: 'chatColor', title: 'title', portrait_frame: 'portraitFrame', card_back: 'cardBack' };
      const slot = slotMap[category];
      if (!slot) { flash('Unknown cosmetic slot', true); setBusy(false); return; }
      await axios.post(`${API}/api/billing/equip-cosmetic`, { cosmeticId, slot }, authHeaders());
      flash('Equipped!', false);
      if (onCharacterUpdate) onCharacterUpdate();
      fetchData();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const handleBuyCrowns = async (packageId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/billing/purchase-crowns`, { packageId }, authHeaders());
      // v637: server returns `sessionUrl`
      const url = res.data.sessionUrl || res.data.url;
      if (url) {
        window.open(url, '_blank');
        flash('Redirecting to payment...', false);
      } else {
        flash('Payment session unavailable.', true);
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to start purchase', true);
    }
    setBusy(false);
  };

  const handleSubscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/billing/subscribe-patron`, {}, authHeaders());
      // v637: server returns `sessionUrl`
      const url = res.data.sessionUrl || res.data.url;
      if (url) {
        window.open(url, '_blank');
        flash('Redirecting to subscription...', false);
      } else {
        flash('Subscription session unavailable.', true);
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const handleCancelPatron = async () => {
    if (busy) return;
    if (!window.confirm('Cancel your Patron subscription? You will keep benefits until the current period ends.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/billing/cancel-patron`, {}, authHeaders());
      flash('Subscription will cancel at period end.', false);
      fetchData();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const handleSubscribeChronicler = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/billing/subscribe-chronicler`, {}, authHeaders());
      const url = res.data.sessionUrl || res.data.url;
      if (url) {
        window.open(url, '_blank');
        flash('Redirecting to subscription...', false);
      } else {
        flash('Subscription session unavailable.', true);
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const handleCancelChronicler = async () => {
    if (busy) return;
    if (!window.confirm('Cancel your Chronicler subscription? Your endless saga pauses and Patron perks end when the period closes.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/billing/cancel-chronicler`, {}, authHeaders());
      flash('Subscription will cancel at period end.', false);
      fetchData();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed', true);
    }
    setBusy(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  const ownedCosmetics = status?.cosmetics || [];
  const activeCosmetics = status?.activeCosmetics || {};

  // v635: Patron 10% Crown Shop discount; Chronicler adds up to +10% via streak.
  const isPatron = status?.patronStatus === 'active' ||
    (status?.patronStatus === 'canceled' && status?.patronUntil && new Date(status.patronUntil) > new Date());
  const isChronicler = status?.chroniclerStatus === 'active' ||
    (status?.chroniclerStatus === 'canceled' && status?.chroniclerUntil && new Date(status.chroniclerUntil) > new Date());
  // Server computes the combined (Patron + streak) discount; fall back gracefully.
  const discountPct = status?.discountPct ?? (isPatron ? (shopData?.patronDiscountPct || 10) : 0);
  const priceFor = (baseCost) => discountPct
    ? Math.max(1, Math.ceil(baseCost * (1 - discountPct / 100)))
    : baseCost;

  const renderItems = () => {
    if (!shopData?.items) return <div className="cs-loading">Loading...</div>;
    return (
      <div className="cs-grid">
        {shopData.items.map(item => {
          const finalCost = priceFor(item.cost);
          return (
          <div key={item.id} className="cs-card">
            <div className="cs-card-name">{item.name}</div>
            <div className="cs-card-cost">
              {discountPct > 0 && finalCost < item.cost && (
                <span className="cs-card-cost-original">👑 {item.cost}</span>
              )}
              👑 {finalCost}
            </div>
            <button
              className="cs-buy-btn"
              disabled={busy || crowns < finalCost}
              onClick={() => handleBuyItem(item.id)}
            >
              {crowns < finalCost ? 'Not enough' : 'Buy'}
            </button>
          </div>
          );
        })}
      </div>
    );
  };

  const renderCosmetics = () => {
    if (!shopData?.cosmetics) return <div className="cs-loading">Loading...</div>;
    const categories = ['chat_color', 'title', 'portrait_frame', 'card_back'];
    const catLabels = { chat_color: 'Chat Colors', title: 'Titles', portrait_frame: 'Portrait Frames', card_back: 'Card Backs' };
    return (
      <div className="cs-cosmetics-list">
        {categories.map(cat => {
          const items = shopData.cosmetics.filter(c => c.category === cat);
          if (!items.length) return null;
          return (
            <div key={cat} className="cs-cosmetic-group">
              <h3 className="cs-group-title">{catLabels[cat] || cat}</h3>
              <div className="cs-grid">
                {items.map(c => {
                  const owned = ownedCosmetics.includes(c.id);
                  const isActive = activeCosmetics[c.category] === c.id;
                  const finalCost = priceFor(c.cost);
                  return (
                    <div key={c.id} className={`cs-card cosmetic${owned ? ' owned' : ''}${isActive ? ' active' : ''}`}>
                      <div className="cs-card-name">
                        {c.category === 'chat_color' && <span style={{ color: c.value }}>■ </span>}
                        {c.name}
                      </div>
                      {!owned ? (
                        <>
                          <div className="cs-card-cost">
                            {discountPct > 0 && finalCost < c.cost && (
                              <span className="cs-card-cost-original">👑 {c.cost}</span>
                            )}
                            👑 {finalCost}
                          </div>
                          <button
                            className="cs-buy-btn"
                            disabled={busy || crowns < finalCost}
                            onClick={() => handleBuyCosmetic(c.id)}
                          >
                            {crowns < finalCost ? 'Not enough' : 'Unlock'}
                          </button>
                        </>
                      ) : (
                        <button
                          className={`cs-equip-btn${isActive ? ' active' : ''}`}
                          disabled={busy}
                          onClick={() => handleEquipCosmetic(isActive ? null : c.id, c.category)}
                        >
                          {isActive ? '✓ Active' : 'Equip'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCrowns = () => (
    <div className="cs-crowns-section">
      <p className="cs-crowns-balance">Your Balance: <span className="cs-gold">👑 {formatCurrency(crowns)}</span></p>
      <div className="cs-grid packages">
        {packages.map(pkg => (
          <div key={pkg.id} className="cs-card package">
            <div className="cs-card-name">{pkg.label}</div>
            <div className="cs-card-price">${(pkg.priceUsd / 100).toFixed(2)}</div>
            <button className="cs-buy-btn crown" disabled={busy} onClick={() => handleBuyCrowns(pkg.id)}>
              Purchase
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPatron = () => {
    return (
      <div className="cs-patron-section">
        <div className="cs-patron-header">
          <h3>⭐ Patron Subscription — $4.99/month<TooltipBadge id="patron.perks" /></h3>
        </div>
        <ul className="cs-patron-perks">
          <li>+50% XP and Gold from all combat</li>
          <li>2 free Delve runs per day (instead of 1)</li>
          <li>★ Patron badge in chat</li>
          <li>200 bonus Crowns every month</li>
          <li>Exclusive Patron title</li>
          <li><strong>10% off everything in the Crown Shop</strong></li>
        </ul>
        {isPatron ? (
          <div className="cs-patron-active">
            <p className="cs-patron-status">✓ You are a Patron!</p>
            {status?.patronStatus === 'canceled' && (
              <p className="cs-patron-note">Subscription ends: {new Date(status.patronUntil).toLocaleDateString()}</p>
            )}
            {status?.patronStatus === 'active' && (
              <button className="cs-cancel-btn" disabled={busy} onClick={handleCancelPatron}>
                Cancel Subscription
              </button>
            )}
          </div>
        ) : (
          <button className="cs-subscribe-btn" disabled={busy} onClick={handleSubscribe}>
            Subscribe Now
          </button>
        )}
      </div>
    );
  };

  const renderChronicler = () => {
    const streakLevel = status?.streakLevel || 0;
    return (
      <div className="cs-patron-section">
        <div className="cs-patron-header">
          <h3>📜 Chronicler Subscription — $7.99/month</h3>
        </div>
        <p className="cs-patron-note">
          An endless, personalized saga written for your character by the Chronicler.
          Every quest you complete branches the story — choices you made ten quests ago
          still echo through what comes next. Includes <strong>every Patron perk</strong>.
        </p>
        <ul className="cs-patron-perks">
          <li><strong>Infinite AI quests</strong> tailored to your class &amp; advanced class</li>
          <li>Branching storyline with lasting consequences (up to 25 quests/day)</li>
          <li>Real Gold &amp; XP rewards scaled to your level</li>
          <li><strong>Daily Streak badge</strong> on your character sheet — climb 10 tiers</li>
          <li>Each streak tier = <strong>+1% Crown Shop discount</strong> (stacks with Patron, up to 20%)</li>
          <li>Everything Patron includes: +50% XP/Gold, 2 free Delves, 200 Crowns/mo, badge &amp; title</li>
        </ul>
        {isChronicler ? (
          <div className="cs-patron-active">
            <p className="cs-patron-status">✓ You are a Chronicler!</p>
            {streakLevel > 0 && (
              <p className="cs-patron-note">Current streak tier: {streakLevel} (+{streakLevel}% shop discount)</p>
            )}
            {status?.chroniclerStatus === 'canceled' && (
              <p className="cs-patron-note">Subscription ends: {new Date(status.chroniclerUntil).toLocaleDateString()}</p>
            )}
            {status?.chroniclerStatus === 'active' && (
              <button className="cs-cancel-btn" disabled={busy} onClick={handleCancelChronicler}>
                Cancel Subscription
              </button>
            )}
          </div>
        ) : (
          <>
            {isPatron && (
              <p className="cs-patron-note" style={{ color: '#c9a84c' }}>
                Note: cancel your Patron subscription first — Chronicler already includes every Patron perk.
              </p>
            )}
            <button className="cs-subscribe-btn" disabled={busy || isPatron} onClick={handleSubscribeChronicler}>
              Subscribe Now
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="cs-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="cs-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-header">
          <h2>👑 Crown Shop</h2>
          <div className="cs-crown-balance">👑 {formatCurrency(crowns)}</div>
          <button className="cs-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
        </div>

        {message && (
          <div className={`cs-flash${message.isError ? ' error' : ' success'}`}>
            {message.text}
          </div>
        )}

        <div className="cs-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`cs-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="cs-body">
          {tab === 'items' && renderItems()}
          {tab === 'cosmetics' && renderCosmetics()}
          {tab === 'crowns' && renderCrowns()}
          {tab === 'patron' && renderPatron()}
          {tab === 'chronicler' && renderChronicler()}
        </div>
      </div>
    </div>
  );
};

export default CrownShopModal;
